import cn from 'classnames'
import React, { useCallback, useMemo } from 'react'

import { IconButton } from 'components/uiKit/Button'
import Tooltip from 'components/uiKit/Dropdown/Tooltip'
import DropdownMenu from 'components/uiKit/DropdownMenu'
import Icon, { type KeyIconsType } from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import Loader from 'components/uiKit/Loader'
import type { IMenuOption } from 'components/uiKit/Menu/MenuItem'
import { preventDefault, stopPropagation } from 'constants/commonConstans'
import { testProps } from 'utils/test/qaData'

import * as s from './CardStandard.module.scss'

export interface ICardStandardAction {
  value: string
  label: string
  onClick: () => void
  hidden?: boolean
  icon?: KeyIconsType
}

interface ICardStandardProps {
  name: string
  icon?: KeyIconsType | React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  badge?: React.ReactNode
  children?: React.ReactNode
  actions?: ICardStandardAction[]
  onClick?: (e: React.MouseEvent) => void
  mainActionIcon?: KeyIconsType
  testData?: string
  loading?: boolean
  disabled?: boolean
}

const CardStandard: React.FC<ICardStandardProps> = ({
  name,
  icon,
  title,
  description,
  badge,
  children,
  actions,
  onClick,
  mainActionIcon = 'arrowMoreOpenNewTab',
  testData,
  loading,
  disabled,
}) => {
  const handleClick = disabled ? undefined : onClick

  const visibleActions = useMemo(() => (actions ?? []).filter((item) => !item.hidden), [actions])

  const actionOptions = useMemo<IMenuOption<string>[]>(
    () =>
      visibleActions.map(({ value, label, icon: itemIcon }) => ({
        value,
        label,
        icon: itemIcon,
      })),
    [visibleActions],
  )

  const handleActionClick = useCallback(
    ({ value }: IMenuOption<string>) => {
      visibleActions.find((item) => item.value === value)?.onClick()
    },
    [visibleActions],
  )

  const isInteractive = !disabled && (Boolean(onClick) || visibleActions.length > 0)
  const hasSingleAction = !disabled && visibleActions.length === 1
  const hasMenuActions = !disabled && visibleActions.length > 1
  const showMainActionArrow = !disabled && visibleActions.length === 0 && Boolean(onClick)

  const cnRoot = cn(s.root, {
    [s.active]: isInteractive,
    [s.disabled]: disabled,
  })

  const handlePropagation = (e: React.MouseEvent) => {
    stopPropagation(e)
    preventDefault(e)
  }

  const iconNode =
    typeof icon === 'string' ? <Icon name={icon as KeyIconsType} size={KitSize.S} /> : icon

  const singleAction = hasSingleAction ? visibleActions[0] : null

  return (
    <div className={cnRoot} onClick={handleClick} {...testProps({ el: 'card', name, testData })}>
      {hasMenuActions && (
        <span className={s.actions} onClick={handlePropagation} onMouseDown={handlePropagation}>
          <DropdownMenu
            name={`${name}.actions`}
            onClick={handleActionClick}
            options={actionOptions}
            placement='bottom-end'
            stopPropagation
          >
            <IconButton
              icon='iconsOtherMore'
              name={`${name}.actions`}
              size={KitSize.S}
              styleType='ghost'
              testData={testData}
            />
          </DropdownMenu>
        </span>
      )}

      {singleAction && (
        <span className={s.actions} onClick={handlePropagation} onMouseDown={handlePropagation}>
          <Tooltip overlay={singleAction.label}>
            <IconButton
              icon={singleAction.icon ?? 'iconsOtherMore'}
              name={`${name}.actions.${singleAction.value}`}
              onClick={singleAction.onClick}
              size={KitSize.S}
              styleType='ghost'
              testData={testData}
            />
          </Tooltip>
        </span>
      )}

      {showMainActionArrow && (
        <span className={s.mainActionArrow} aria-hidden>
          <Icon name={mainActionIcon} size={KitSize.XS} />
        </span>
      )}

      {loading ? (
        <Loader name='cardStandardLoader' />
      ) : (
        <>
          <div className={s.header}>
            <div className={s.headerTop}>
              {iconNode && <span className={s.iconSlot}>{iconNode}</span>}
              <div className={s.title}>{title}</div>
            </div>
            {description && <div className={s.description}>{description}</div>}
            {badge && <div>{badge}</div>}
          </div>
          {children && <div className={s.footer}>{children}</div>}
        </>
      )}
    </div>
  )
}

export default CardStandard
